import { useApp, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import createUncachedLorebookSearchConfig from "@next-platform/client-common-core/lorebook/createUncachedLorebookSearchConfig";
import { LorebookSelectionSort } from "@next-platform/client-common-core/lorebook/selection";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconVariants,
  Input,
  ListEmpty,
  LorebookCard,
  Spinner,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Lorebook, LorebookVisibility } from "@next-platform/packages-ts-types";
import { useEffect, useMemo, useState } from "react";
import { Configure, ConfigureProps, InstantSearch, useInfiniteHits, useInstantSearch, useSearchBox, useStats } from "react-instantsearch";
import InstantSearchAdapter, { TypesenseInstantsearchAdapterOptions } from "typesense-instantsearch-adapter";

import useShouldFilterNsfwCharacters from "@/components/search/hooks/useShouldFilterNsfwCharacters";
import { SearchAdapter } from "@/components/search/types";
import { createSearchClientConfig } from "@/components/search/utils/createSearchClientConfig";
import Trans from "@/components/Trans/Trans";
import {
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS,
  NSFW_TOGGLE_LOCALSTORAGE_KEY,
  TYPESENSE_TEXT_MATCH_SORT_PREFIX,
} from "@/constants";
import { Collection, TypesenseApiKey, TypesenseCollectionName } from "@/types/api";
import { getAvatarUrl } from "@/utils/getAvatarUrl";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import createLorebookDiscoverFilters from "./createLorebookDiscoverFilters";

interface ILorebookDiscoverTabProps {
  selectedLorebook: Lorebook | null;
  onSelect: (lorebook: Lorebook) => void;
  renderFilterControl?: () => React.ReactNode;
  renderSortControl?: () => React.ReactNode;
  sortOrder: LorebookSelectionSort;
  favoriteIds?: string[] | null;
}

// Shape of a lorebook document returned by the Typesense lorebooks index.
// Fields mirror DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS.
interface ILorebookHit {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  lorebook_id?: string;
  id?: string;
  name?: string;
  description?: string;
  tags?: string[];
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  creator_username?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  creator_user_id?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  avatar_url?: string;
  visibility?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  is_nsfw?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  avatar_is_nsfw?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense documents use snake_case field names
  num_entries?: number;
}

// Map a Typesense lorebook hit to a Lorebook entity so it can flow through the
// same attach callback the "My Lorebooks" tab uses.
function mapHitToLorebook(hit: ILorebookHit): Lorebook {
  const id = hit.lorebook_id ?? hit.id ?? "";
  return {
    id,
    name: hit.name || "",
    description: hit.description ?? "",
    avatar_url: hit.avatar_url ?? "",
    tags: hit.tags ?? [],
    creator_user_id: hit.creator_user_id ?? "",
    creator_username: hit.creator_username ?? "",
    visibility: (hit.visibility as LorebookVisibility) ?? LorebookVisibility.PUBLIC,
    is_nsfw: Boolean(hit.is_nsfw),
    avatar_is_nsfw: Boolean(hit.avatar_is_nsfw),
    num_entries: hit.num_entries ?? 0,
  } as Lorebook;
}

interface ILorebookDiscoverResultsProps {
  selectedLorebook: Lorebook | null;
  onSelect: (lorebook: Lorebook) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  renderFilterControl?: () => React.ReactNode;
  renderSortControl?: () => React.ReactNode;
}

function LorebookDiscoverResults({
  selectedLorebook,
  onSelect,
  searchQuery,
  onSearchQueryChange,
  renderFilterControl,
  renderSortControl,
}: ILorebookDiscoverResultsProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useUserInfo();
  const { blurNSFW } = useUserSettings();
  const { refine } = useSearchBox();
  const { items, isLastPage, showMore } = useInfiniteHits<ILorebookHit>();
  const { nbHits } = useStats();
  const { status } = useInstantSearch();
  // Blur NSFW avatars for the same audience as the rest of the app: signed-out users always,
  // and signed-in users who have the "blur NSFW" setting on.
  const shouldBlurNsfwAvatar = !isAuthenticated || blurNSFW;
  const resultsCountValues = useMemo(() => ({ count: nbHits }), [nbHits]);
  // Only treat an empty result set as "no results" once the search has settled; while a
  // request is in flight `items` is momentarily empty, which would otherwise flash the
  // empty state before results arrive.
  const isSearching = status === "loading" || status === "stalled";

  useEffect(() => {
    refine(searchQuery);
  }, [searchQuery, refine]);

  const renderBody = () => {
    if (items.length) {
      return (
        <div className="flex flex-col gap-md">
          {items.map((hit) => {
            const lorebook = mapHitToLorebook(hit);
            return (
              <LorebookCard
                key={lorebook.id}
                id={lorebook.id}
                avatarUrl={getAvatarUrl(lorebook.avatar_url ?? "", { returnWithOriginalRatio: true }) as string}
                name={lorebook.name}
                isPrivate={lorebook.visibility === LorebookVisibility.PRIVATE}
                isBlurred={Boolean(shouldBlurNsfwAvatar && lorebook.avatar_is_nsfw)}
                description={lorebook.description}
                numEntries={lorebook.num_entries ?? 0}
                creatorUsername={lorebook.creator_username}
                onClick={() => onSelect(lorebook)}
                isSelected={selectedLorebook?.id === lorebook.id}
              />
            );
          })}
          {!isLastPage ? (
            <Button type="button" variant={ButtonVariant.OUTLINED} size={ButtonSize.MEDIUM} className="self-center" onClick={showMore}>
              <Trans i18nKey="common:loadMore" />
            </Button>
          ) : null}
        </div>
      );
    }

    if (isSearching) {
      return (
        <div className="flex flex-1 justify-center items-center">
          <Spinner className="mx-auto" />
        </div>
      );
    }

    return (
      <div className="flex flex-1 justify-center items-center">
        <ListEmpty
          isLoading={false}
          title={t("common:noResultsFound")}
          description={t("lorebook:modal.attachLorebook.emptyState.noResults.description")}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 gap-4">
      <Input
        placeholder={t("myCreation:search.lorebooks")}
        iconStart={IconVariants.SEARCH}
        value={searchQuery}
        onChange={(value) => onSearchQueryChange(value ?? "")}
      />
      <div className="flex justify-between items-center">
        {items.length ? (
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
            <Trans i18nKey="common:results.found" values={resultsCountValues} />
          </Text>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {renderFilterControl?.()}
          {renderSortControl?.()}
        </div>
      </div>
      {renderBody()}
    </div>
  );
}

export default function LorebookDiscoverTab({
  selectedLorebook,
  onSelect,
  renderFilterControl,
  renderSortControl,
  sortOrder,
  favoriteIds,
}: ILorebookDiscoverTabProps) {
  const { typesenseConfig } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const isFavoritesMode = Array.isArray(favoriteIds);
  const { showNSFW, areUserSettingsLoaded, areUserSettingLoading } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const [stateSearchQuery, setStateSearchQuery] = useState("");
  const [stateAdapter, setStateAdapter] = useState<SearchAdapter | null>(null);

  // Match SearchClientCharacterListing: for signed-in users, don't query Typesense until their
  // settings load, so NSFW filtering/blur use the real showNSFW/blurNSFW values rather than the
  const shouldDelayQuery = isAuthenticated && !areUserSettingsLoaded && areUserSettingLoading !== FetchStatus.failed;

  // The lorebooks collection has no name_i18n field in its Typesense schema, so names are
  // queried and returned in their original language regardless of the user's chat language.
  const queryBy = DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS;
  const includeFields = DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS;

  // Match public character and lorebook browsing. This modal has no NSFW toggle,
  // so it uses the persisted browsing toggle with the shared capability decision.
  const isNsfwToggled = typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY) === "true";
  const { shouldFilterNsfwCharacters: shouldFilterNsfwLorebooks } = useShouldFilterNsfwCharacters({
    isNsfwToggled,
    isShowNsfwEnabled: showNSFW,
  });

  const apiKey = typesenseConfig?.[TypesenseApiKey[Collection.LOREBOOKS]];
  const baseIndexName = typesenseConfig?.[TypesenseCollectionName[Collection.LOREBOOKS]] ?? "";
  // Sort is applied via the Typesense index name (relevance prefix + createdAt direction),
  // matching how SearchClientCharacterListing / SearchBySort build their sort indices.
  const createdAtSort = sortOrder === LorebookSelectionSort.Oldest ? "createdAt:asc" : "createdAt:desc";
  const indexName = `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},${createdAtSort}`;

  useEffect(() => {
    if (!apiKey || shouldDelayQuery) return;
    const searchClientConfig = createSearchClientConfig({
      apiKey,
      queryBy,
      includeFields,
    });
    const configuration = createUncachedLorebookSearchConfig(searchClientConfig);
    const adapter = new InstantSearchAdapter(configuration as TypesenseInstantsearchAdapterOptions) as SearchAdapter;
    setStateAdapter(adapter);
  }, [apiKey, queryBy, includeFields, shouldDelayQuery]);

  const filters = useMemo(
    () =>
      createLorebookDiscoverFilters({
        applicationId,
        favoriteIds,
        isFavoritesMode,
        shouldFilterNsfwLorebooks,
      }),
    [applicationId, favoriteIds, isFavoritesMode, shouldFilterNsfwLorebooks],
  );

  const configProps = useMemo(
    () =>
      ({
        hitsPerPage: 48,
        filters,
      }) as ConfigureProps,
    [filters],
  );

  if (!apiKey || shouldDelayQuery || !stateAdapter?.searchClient) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Spinner className="mx-auto" />
      </div>
    );
  }

  return (
    <InstantSearch searchClient={stateAdapter.searchClient} indexName={indexName}>
      <Configure {...configProps} />
      <LorebookDiscoverResults
        selectedLorebook={selectedLorebook}
        onSelect={onSelect}
        searchQuery={stateSearchQuery}
        onSearchQueryChange={setStateSearchQuery}
        renderFilterControl={renderFilterControl}
        renderSortControl={renderSortControl}
      />
    </InstantSearch>
  );
}
